import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import WithWizard from 'modules/views/shared/Wizard/WithWizard';
import Warning from 'modules/views/shared/Warning';
import CapturePhoto from 'modules/views/shared/CapturePhoto';
import NavigationButtons from 'modules/views/shared/NavigationButtons';
import { ReactComponent as GreenCheck } from 'assets/icons/SuccessCheck.svg';
import { ReactComponent as CrossIcon } from 'assets/icons/ErrorCheck.svg';
import avoidCapImg from 'assets/images/AvoidCap.png';
import avoidMaskImg from 'assets/images/AvoidMask.png';
import validPhotoImg from 'assets/images/ValidPhoto.png';

import { useWizardStepNavigation } from 'modules/views/shared/Wizard';
import useUploadProfilePicture from 'hooks/useUploadProfilePicture';
import { useAppSelector } from 'modules/state-mgmt/store';
import { WorkerProfileField } from 'types/profileCustomFields';

import { useStyles } from './styles';

const TakePhoto = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { goToNextStep, handlePreviousStep } = useWizardStepNavigation();
  const { hasSignatureUrl, profileFieldConstraintsMap } = useAppSelector(state => ({
    hasSignatureUrl: !!(state.onboarding as any)?.signatureUrl,
    profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap,
  }));

  const isPhotoSkippable = profileFieldConstraintsMap[WorkerProfileField.workerProfilePhoto]?.isSkippable || false;

  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const { isLoading, uploadProfilePicture } = useUploadProfilePicture({
    successCallback: () => {
      goToNextStep();
    },
  });

  const onProceed = (imageSrc: string | string[]) => {
    const imageSource = Array.isArray(imageSrc) ? imageSrc[0] : imageSrc;
    uploadProfilePicture({ imageSrc: imageSource });
  };

  const content = (
    <div>
      <ContentHeader title={t('takePhoto.title')} subtitle={t('takePhoto.subtitle')} />
      <Instruction
        badImage={avoidMaskImg}
        goodImage={validPhotoImg}
        title={t('takePhoto.instructions.0.title')}
        text={t('takePhoto.instructions.0.text')}
      />
      <Instruction
        badImage={avoidCapImg}
        goodImage={validPhotoImg}
        title={t('takePhoto.instructions.1.title')}
        text={t('takePhoto.instructions.1.text')}
      />
      <div className={classes.warningContainer}>
        <Warning text={t('takePhoto.warning')} />
      </div>
      <NavigationButtons
        secondaryButtonProps={{
          isHidden: !isPhotoSkippable && hasSignatureUrl,
          label: isPhotoSkippable ? t('takePhoto.buttons.skip') : undefined,
          onClick: isPhotoSkippable ? () => goToNextStep() : handlePreviousStep,
          testId: 'take-photo-secondary-button',
        }}
        primaryButtonProps={{
          label: t('takePhoto.buttons.capture'),
          isLoading,
          hasEndArrow: true,
          styleClass: classes.captureButton,
          testId: 'take-photo-capture-button',
          onClick: () => setShowCaptureModal(true),
        }}
      />
      {showCaptureModal && (
        <CapturePhoto isProceedLoading={isLoading} onClose={() => setShowCaptureModal(false)} onProceed={onProceed} />
      )}
    </div>
  );

  return <WithWizard disableNavigation={isLoading} content={content} />;
};

export default memo(TakePhoto);

interface IInstructionProps {
  title: string;
  text: string;
  badImage: string;
  goodImage: string;
}

const Instruction = ({ title, text, badImage, goodImage }: IInstructionProps) => {
  const classes = useStyles();
  return (
    <div className={classes.instructionContainer}>
      <div className={classes.imagePairContainer}>
        <div className={classes.imageWrapper}>
          <img src={badImage} alt="" className={classes.instructionImage} />
          <div className={classes.badge}>
            <CrossIcon />
          </div>
        </div>
        <div className={classes.imageWrapper}>
          <img src={goodImage} alt="" className={classes.instructionImage} />
          <div className={classes.badge}>
            <GreenCheck />
          </div>
        </div>
      </div>
      <p className={classes.instructionTitle}>{title}</p>
      <p className={classes.instructionText}>{text}</p>
    </div>
  );
};
